/**
 * @file Registre client des moments de réservation (midi/soir).
 * @module packages.common.utils.moments
 *
 * Miroir de `backend/.../services/core/momentRegistry.js` : le backend reste
 * l'autorité (validation des clés à l'écriture), le client ne fait qu'afficher
 * et ordonner. Les clés coïncident avec les noms de service historiquement
 * persistés (`midi`, `soir`).
 *
 * Règle produit (refonte Services & Moments, décision D1) : les moments et
 * leurs libellés sont définis UNIQUEMENT par Brydge, aucun texte libre
 * restaurateur n'entre jamais dans cette liste. Ouvrir un nouveau moment
 * (ex. brunch) = ajout ici ET dans le registre backend, jamais une saisie.
 */

export const MOMENTS = Object.freeze([
  Object.freeze({
    key: 'midi',
    labels: Object.freeze({fr: 'Midi', en: 'Lunch', nl: 'Middag', de: 'Mittag'}),
    displayOrder: 1,
  }),
  Object.freeze({
    key: 'soir',
    labels: Object.freeze({fr: 'Soir', en: 'Dinner', nl: 'Avond', de: 'Abend'}),
    displayOrder: 2,
  }),
]);

/** Clés de moments valides, dans l'ordre d'affichage. */
export const MOMENT_KEYS = Object.freeze(MOMENTS.map((m) => m.key));

/** Jours de semaine dans l'ordre canonique du planning (aligné backend). */
export const WEEK_DAYS = Object.freeze(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']);

/**
 * Retourne la définition d'un moment par sa clé (tolérant à la casse).
 *
 * @param {string} key - Clé du moment (ex. 'midi')
 * @return {Object|null} Définition du moment ou null si inconnue
 */
export function getMomentByKey(key) {
  if (typeof key !== 'string') return null;
  const normalized = key.trim().toLowerCase();
  return MOMENTS.find((m) => m.key === normalized) || null;
}

/**
 * Retourne le libellé client d'un moment dans une langue donnée.
 *
 * @param {string} key - Clé du moment
 * @param {string} language - Code langue ('fr', 'en', 'nl', 'de')
 * @return {string} Libellé traduit (repli français), ou la clé si moment inconnu
 */
export function getMomentLabel(key, language = 'fr') {
  const moment = getMomentByKey(key);
  if (!moment) return key;
  return moment.labels[language] || moment.labels.fr;
}

/**
 * Trie des objets porteurs d'une clé de moment selon l'ordre d'affichage du registre.
 *
 * @param {Array<Object>} items - Éléments à trier
 * @param {Function} [getKey] - Extracteur de clé (défaut : item.momentKey)
 * @return {Array<Object>} Nouveau tableau trié
 */
export function sortByMomentOrder(items, getKey = (item) => item.momentKey) {
  return [...(items || [])].sort((a, b) => {
    const orderA = getMomentByKey(getKey(a))?.displayOrder ?? 99;
    const orderB = getMomentByKey(getKey(b))?.displayOrder ?? 99;
    return orderA - orderB;
  });
}
